import React, { useCallback } from 'react';
import useResourceSetup from 'new/components/flows/types/spark/CreateSparkFlow/ResourceSetup/useResourceSetup';
import { Stack } from 'new/elements/Stack';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { FileSystem } from 'new/components/flows/configure/FileSystem';
import { FORM_TYPE_CREATE_SPARK } from 'js/constants/formTypes';
import flowTypes from 'new/components/flows/types/flowTypes';
import useSpark from 'new/components/flows/types/spark/useSpark';
import { CONNECTION_CATEGORY_DATABASE } from 'js/constants/connectionCategories';
import usePaging from 'new/state/paging/repository/usePaging';
import Database from 'new/components/flows/configure/Database';
import { useForm } from 'new/elements/Form/useForm';
import useRequests from 'new/state/request/repository/useRequests';
import RawFileDataCheckbox from 'js/components/data_sources/add_ds_form/RawFileDataCheckbox';
import { CustomUserConfirmation } from 'js/components/route_utils/UserConfirmation';

export const ResourceSetup = () => {
  const { nextPage } = useForm();
  const {
    selectedConnection,
    selectedBucket,
    credentialId,
    source,
    nexset,
    sink,
    sinkConfig,
    connectionType,
    table,
    isNewTable,
    isEditMode,
    connectionCategory,
    sourceConfig,
    getSourceConfigFields,
  } = useSpark();
  const sourceConfigFields = getSourceConfigFields(selectedConnection, flowTypes.spark);
  const { paging } = usePaging();
  // Get ALL fields (before flow type exclusion) to ensure fields like ai.function
  // can be added when they should render, even if excluded for the flow type
  const allSourceConfigFields = paging.allSourceConfigById?.[selectedConnection] || [];
  const {
    onSourceConfigChanged,
    allowCustomPrefixEditor,
    nexsetSchema,
    initialLoading,
    isRedirecting,
    onSinkConfigChanged,
    onTableSelected,
    onSinkMappingChanged,
    onBucketSelected,
    onSaveAdvancedMode,
    showSaveConfirmation, // Save confirmation state and functions
    handleSaveConfirmation,
    onCloseSaveConfirmation,
  } = useResourceSetup();
  const { showError } = useRequests();
  const onError = useCallback((msg: string) => showError(msg), []);

  if (initialLoading || isRedirecting) {
    return (
      <Stack padding="50px">
        <CenteredLoader />
      </Stack>
    );
  }

  if (!connectionType) return null;
  if (connectionType === 'SINK' && !sinkConfig) return null;
  if (connectionType === 'SOURCE' && !sourceConfig) return null;

  // Render main content
  let mainContent;
  if (connectionCategory === CONNECTION_CATEGORY_DATABASE) {
    mainContent = (
      <Database
        canCreateTable={false}
        flowType={flowTypes.spark}
        nexset={nexset}
        sink={sink}
        formType={FORM_TYPE_CREATE_SPARK}
        selectedConnection={selectedConnection}
        nexsetSchema={nexsetSchema}
        selectedBucket={selectedBucket}
        selectedTable={table}
        onSinkConfigChanged={onSinkConfigChanged}
        onTableSelected={onTableSelected}
        onSourceConfigChanged={onSourceConfigChanged}
        onSinkMappingUpdated={onSinkMappingChanged}
        sinkConfig={sinkConfig}
        isNewTable={isNewTable}
        isEditMode={isEditMode}
        credentialId={credentialId}
        source={source}
        connectionType={connectionType}
        sourceConfig={sourceConfig}
        nextFormStage={() => {}}
        onSaveAdvancedMode={onSaveAdvancedMode}
      />
    );
  } else {
    mainContent = (
      <FileSystem
        flowType={flowTypes.spark}
        connectionType={connectionType}
        sink={sink}
        formType={FORM_TYPE_CREATE_SPARK}
        source={source}
        nexset={nexset}
        nexsetSchema={nexsetSchema}
        selectedBucket={selectedBucket}
        allowCustomPrefixEditor={allowCustomPrefixEditor}
        selectedConnection={selectedConnection}
        credentialId={credentialId}
        sinkConfig={sinkConfig}
        sourceConfig={sourceConfig}
        onBucketSelected={onBucketSelected}
        onSourceConfigChanged={onSourceConfigChanged}
        onSinkConfigChanged={onSinkConfigChanged}
        onSaveAdvancedMode={onSaveAdvancedMode}
        nextFormStage={nextPage}
        onError={onError}
        /**
          The new Form component already has a loading spinner.
          We don't need to show again in the component, so we pass false
          It is automatically true for Flex Flow, since it uses the old Form component
        */
        shouldShowLoading={false}
        renderSectionFields={(section, fields) => {
          return (
            <>
              {sourceConfig && (
                <RawFileDataCheckbox
                  section={section}
                  sourceConfig={sourceConfig}
                  sourceConfigFields={allSourceConfigFields}
                  onSourceConfigChanged={onSourceConfigChanged}
                />
              )}
              {fields}
            </>
          );
        }}
      />
    );
  }

  // Render SaveConfirmation modal alongside main content
  if (showSaveConfirmation) {
    return (
      <>
        {mainContent}
        <CustomUserConfirmation
          title="Save Changes to Active Source"
          message="You've made changes to an active source. Choose how you want to apply these changes:"
          okButtonLabel="Save for Next Run"
          cancelButtonLabel="Cancel"
          onConfirm={() => handleSaveConfirmation(true)}
          onClose={onCloseSaveConfirmation}
          yesWithChangesButtonLabel="Save & Run Now"
          onRunWithIngestion={() => handleSaveConfirmation(false)}
        />
      </>
    );
  }

  return mainContent;
};

ResourceSetup.formConfig = {
  title: 'Configure',
};
